import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { TaskView } from 'src/components/task/task-view';
import { TaskScope } from 'src/models/global-enum';
import { TaskItem, TaskItemTypeEnum } from 'src/models/task-item.model';
import {
  EditTask,
  WorkOnTask,
  TaskSettings,
  TaskInfo,
  TaskItemOption,
  MultiSelectTaskItemOption,
  ReassignTaskRequest,
} from 'src/models/task.model';
import { UpdateTaskItemRequest } from 'src/services/task-item.service';
import { TaskService } from 'src/services/task.service';
import { withRequestStatus } from './features/request-status.feature';
import { GeneralStore } from './general.store';

export type TaskState = {
  currentEditTask: EditTask | null;
  currentWorkOnTask: WorkOnTask | null;
  currentTaskSettings: TaskSettings | null;
  currentTaskInfo: TaskInfo | null;
};

const getViewOrScope = <T extends typeof TaskView | typeof TaskScope>(
  taskInfo: TaskInfo | null,
  viewOrScope: T,
): T[keyof T] | null => {
  if (!taskInfo) return null;
  if (taskInfo?.isFailed) return viewOrScope.FAILED as T[keyof T];
  if (taskInfo?.isCompleted) return viewOrScope.COMPLETED as T[keyof T];
  if (taskInfo?.isSpawned) return viewOrScope.SPAWNED as T[keyof T];
  if (taskInfo?.isDeployed) return viewOrScope.DEPLOYED as T[keyof T];
  if (taskInfo?.isLocal) return viewOrScope.LOCAL as T[keyof T];
  return viewOrScope.GLOBAL as T[keyof T];
};

export const TaskStore = signalStore(
  { providedIn: 'root' },
  withRequestStatus(),
  withState<TaskState>({
    currentEditTask: null,
    currentWorkOnTask: null,
    currentTaskSettings: null,
    currentTaskInfo: null,
  }),
  withComputed(({ currentEditTask, currentTaskInfo }) => {
    const taskScope = computed(() => getViewOrScope(currentTaskInfo(), TaskScope));
    return {
      taskScope,
      taskView: computed(() => getViewOrScope(currentTaskInfo(), TaskView)),
      taskIsInPreview: computed(() => {
        const scope = taskScope();
        return scope
          ? [TaskScope.LOCAL, TaskScope.GLOBAL, TaskScope.DEPLOYED].includes(scope)
          : false;
      }),
      // Current task items with hideScoring property set to true on the item and its options if the item is a child of a radio list
      currentEditTaskItems: computed(() => {
        const doesItemHaveParentRadioList = (
          currentItem: TaskItem,
          originalItem: TaskItem,
          taskItems: TaskItem[],
        ) => {
          if (
            !currentItem.parentTaskItemID ||
            currentItem.parentTaskItemID === currentItem.taskItemID
          ) {
            return false;
          }
          const parentItem = taskItems.find(
            task => task.taskItemID === currentItem.parentTaskItemID,
          );
          if (!parentItem) {
            return false;
          }
          const parentIsRadioList = parentItem.itemTypeID === TaskItemTypeEnum.RadioList;
          if (parentIsRadioList) {
            // Mark all option scoring as hidden if the item isn't scorable
            originalItem.multiSelectOptions.forEach(
              option => (option = { ...option, hideScoring: true }),
            );
            originalItem.taskItemOptions.forEach(
              option => (option = { ...option, hideScoring: true }),
            );
            return true;
          }
          return doesItemHaveParentRadioList(parentItem, originalItem, taskItems);
        };
        const taskItems = [...(currentEditTask()?.taskItems ?? [])];
        return taskItems?.map((item: TaskItem) => ({
          ...item,
          hideScoring: doesItemHaveParentRadioList(item, item, taskItems),
        }));
      }),
    };
  }),
  withMethods((store, taskService = inject(TaskService), generalStore = inject(GeneralStore)) => ({
    setHasPendingData() {
      patchState(store, { requestStatus: 'pending' });
    },
    setHasCompleteData() {
      patchState(store, { requestStatus: 'complete' });
    },
    updateTaskInfo(updates: Partial<TaskInfo>) {
      if (!store.currentTaskInfo()) return;
      patchState(store, {
        currentTaskInfo: { ...store.currentTaskInfo()!, ...updates },
      });
    },
    updateEditTask(updates: Partial<EditTask>) {
      if (!store.currentEditTask()) return;
      patchState(store, {
        currentEditTask: { ...store.currentEditTask()!, ...updates },
      });
    },
    updateEditTaskItem(
      updatedTaskItem: TaskItem | TaskItemOption | MultiSelectTaskItemOption,
      prompt?: UpdateTaskItemRequest['prompt'],
      optional?: UpdateTaskItemRequest['optional'],
      score?: UpdateTaskItemRequest['score'],
    ) {
      let taskItem = store
        .currentEditTask()
        ?.taskItems.find(
          item =>
            item.id === updatedTaskItem.id ||
            item.id === (updatedTaskItem as MultiSelectTaskItemOption).id,
        );
      if (taskItem && prompt) taskItem = { ...taskItem, prompt };
      if (taskItem && !!optional) taskItem = { ...taskItem, optional: !!optional };
      if (taskItem && score) taskItem = { ...taskItem, score };

      patchState(store, {
        currentEditTask: store.currentEditTask(),
        requestStatus: 'complete',
      });
    },
    deleteEditTaskItem(itemId: number) {
      if (!store.currentEditTask()?.taskItems) return;
      const taskItems = [...store.currentEditTask()!.taskItems];
      taskService.deleteTaskItemFromArray(taskItems, itemId);

      patchState(store, {
        currentEditTask: store.currentEditTask()
          ? { ...store.currentEditTask()!, taskItems }
          : null,
        requestStatus: 'complete',
      });
    },
    updateTaskSettings(updates: Partial<TaskSettings>) {
      if (!store.currentTaskSettings()) return;
      patchState(store, { currentTaskSettings: { ...store.currentTaskSettings()!, ...updates } });
    },
    updateWorkOnTask(updates: Partial<WorkOnTask>) {
      if (!store.currentWorkOnTask()) return;
      patchState(store, { currentWorkOnTask: { ...store.currentWorkOnTask()!, ...updates } });
    },
    updateTaskItem(taskItemId: number, updates: Partial<TaskItem>) {
      if (!store.currentWorkOnTask()) return;
      const taskItems = store
        .currentWorkOnTask()!
        .taskItems.map(item => (item.id === taskItemId ? { ...item, ...updates } : item));
      patchState(store, {
        currentWorkOnTask: { ...store.currentWorkOnTask()!, taskItems },
      });
    },
    reassignAndUpdateWorkOnTask(payload: ReassignTaskRequest) {
      if (!store.currentWorkOnTask()) return;
      taskService.reassignTask(payload).subscribe(() => {
        this.fetchTaskInfo(store.currentWorkOnTask()!.id);
      });
    },
    fetchAllTaskData(taskID: number) {
      this.fetchTaskForEdit(taskID);
      this.fetchTaskForWork(taskID);
      this.fetchTaskSettings(taskID);
      this.fetchTaskInfo(taskID);
    },
    fetchTaskForWork(taskID: number) {
      generalStore.setLoading(true);
      taskService.getTaskForWork(taskID).subscribe((task: WorkOnTask) => {
        task.taskItems = task.taskItems.sort((a: TaskItem, b: TaskItem) => a.order - b.order);
        patchState(store, { currentWorkOnTask: task });
        generalStore.setLoading(false);
      });
    },
    fetchTaskForEdit(taskID: number) {
      generalStore.setLoading(true);
      patchState(store, { requestStatus: 'pending' });
      taskService.getTaskForEdit(taskID).subscribe((task: EditTask) => {
        task.taskItems = task.taskItems?.sort((a: TaskItem, b: TaskItem) => a.order - b.order);
        patchState(store, { currentEditTask: task, requestStatus: 'complete' });
        generalStore.setLoading(false);
      });
    },
    fetchTaskSettings(taskID: number) {
      generalStore.setLoading(true);
      taskService.getTaskSettings(taskID).subscribe((task: TaskSettings) => {
        patchState(store, { currentTaskSettings: task });
        generalStore.setLoading(false);
      });
    },
    fetchTaskInfo(taskID: number) {
      generalStore.setLoading(true);
      taskService.getTaskInfo(taskID).subscribe((task: TaskInfo) => {
        patchState(store, { currentTaskInfo: task });
        generalStore.setLoading(false);
      });
    },
    clearEditTask() {
      patchState(store, { currentEditTask: null });
    },
    clearWorkOnTask() {
      patchState(store, { currentWorkOnTask: null });
    },
    clearTaskSettings() {
      patchState(store, { currentTaskSettings: null });
    },
    clearTaskInfo() {
      patchState(store, { currentTaskInfo: null });
    },
    clearAllTaskData() {
      this.clearEditTask();
      this.clearWorkOnTask();
      this.clearTaskSettings();
      this.clearTaskInfo();
    },
    getCurrentTaskSettings(taskID: number) {
      const taskSettings = store.currentTaskSettings();
      if (!taskSettings) {
        this.fetchTaskSettings(taskID);
      } else if (taskSettings.id !== taskID) {
        this.clearTaskSettings();
        this.fetchTaskSettings(taskID);
      }
      return taskSettings;
    },
  })),
);
